// internal deps
import { isEmpty } from "@swiggy-private/payment-core";
import {
    PaymentMethod,
    PaymentGroupData,
    PaymentGroupMeta,
    PaymentMethodMeta,
    CredPayPaymentGroup,
} from "@swiggy-private/payment-ui";
import { VALID_PAYMENT_GROUP_TYPES, OrderData } from "@swiggy-private/payment-types";
import { getBridgeSdk } from "@swiggy-private/checkout-core";
import { Tenant } from "@swiggy-private/native-bridge/dist/interfaces/Tenant";

//Helpers
import { isPreferredGroup } from "./PaymentHelper";

// interfaces
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";

//constants
import { POLLING_CONFIG } from "@payment-client/constants/Options";
import { isTurboPaymentGroup } from "./TurboPayment";

//constants
const CRED_ELIGIBLITY_TIMEOUT_E1 = 500;
const FLOW_TYPE_INTENT = "INTENT";

type PaymentGroups = GetPaymentMethodsResponse["paymentGroup"];

//Check Wheter CRED group is Enabled
export const isCredInstrumentAvailable = (paymentGroups: PaymentGroups, groupType: string): boolean => {
    const credGroup = paymentGroups.filter((group) => group.groupName.toUpperCase() === groupType);
    const isCredGroupEnabled = credGroup.some(
        (method) => method.groupName.toUpperCase() === groupType && method.enabled == true,
    );
    if (isCredGroupEnabled) {
        return true;
    }

    // checking if instrument exists in preferred or single click group
    const preferredGroup = paymentGroups.find((group) => isPreferredGroup(group) || isTurboPaymentGroup(group));
    if (preferredGroup == null) {
        return false;
    }
    return preferredGroup.paymentMethods.some(
        (method) => method.groupName.toUpperCase() === groupType && method.enabled == true,
    );
};

export function isCredEligiblityValid(
    isSdkAvailable: boolean,
    clientAuthToken: string | undefined,
    paymentGroup: GetPaymentMethodsResponse["paymentGroup"],
): boolean {
    return (
        isSdkAvailable &&
        isCredInstrumentAvailable(paymentGroup, VALID_PAYMENT_GROUP_TYPES.CRED) &&
        !isEmpty(clientAuthToken)
    );
}

export function isCredPayGroup(
    group: PaymentGroupData<PaymentGroupMeta, PaymentMethod<PaymentMethodMeta>>,
): group is CredPayPaymentGroup["paymentData"] {
    return group.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.CRED;
}

export function isCredPaymentMethod(method: PaymentMethod<PaymentMethodMeta>): boolean {
    return [VALID_PAYMENT_GROUP_TYPES.CRED].indexOf(method.groupName.toUpperCase()) !== -1;
}

export async function fetchCredElegiblity(tenant?: string, merchantId?: string): Promise<boolean> {
    return getBridgeSdk()
        .getCredEligibilityFromAppCache(
            {
                ...(!isEmpty(tenant) && { tenant: tenant as Tenant }),
                ...(!isEmpty(merchantId) && { merchantId }),
            },
            CRED_ELIGIBLITY_TIMEOUT_E1,
        )
        .then((data) => {
            if (data == null || data == undefined) {
                return false;
            } else if (data.flowType == null || data.flowType.toUpperCase() != FLOW_TYPE_INTENT) {
                return false;
            } else {
                return data?.isEligible ?? false;
            }
        })
        .catch((_err) => {
            return false;
        });
}

export function getCredPaymentPollingDuration(orderData?: OrderData): number {
    // convert units(minutes) to ms
    return (
        Number(orderData?.credConfirmationTimeMinutes) * 60000 ||
        Number(orderData?.confirmationTimeMinutes) * 60000 ||
        POLLING_CONFIG.defaultCredPollingDuration
    );
}
